import React, { lazy, Suspense } from 'react';
import './App.css';
import './i18n/config';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import MainContent from './pages/MainContent';
import MaintenancePage from './pages/MaintenancePage';
import Home from './pages/Home';
import About from './pages/About';
import WorkWithUs from './pages/WorkWithUs';
import Login from './pages/Login';
import NotFound from './pages/NotFound';
import Registration from './pages/Registration';
import RegistrationConfirm from './pages/RegistrationConfirm';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import TeacherRegistration from './pages/TeacherRegistration';
import { LoadingProvider } from './context/LoadingContext';
import { ErrorProvider } from './context/ErrorContext';
import { ToastProvider } from './context/ToastContext';
import { NotificationsProvider } from './context/NotificationsContext';
import AdminLogin from './pages/admin/Login';
import Loading from './components/Loading';
import TeacherApplication from './pages/TeacherApplication';
import RouteTracker from './components/RouteTracker';
import ExternalRedirect from './components/ExternalRedirect';

// Le pagine delle aree riservate si caricano a parte, solo quando servono: così
// FullCalendar, mappe, grafici e il resto non pesano sul bundle della parte pubblica.
// Durante il caricamento si vede il loader del brand.
const lazyPage = (factory: () => Promise<{ default: React.ComponentType }>) => {
  const LazyComponent = lazy(factory);
  const Page = () => (
    <Suspense fallback={<Loading />}>
      <LazyComponent />
    </Suspense>
  );
  return Page;
};

const AdminHomePage = lazyPage(() => import('./pages/admin/Home'));
const FamilyHome = lazyPage(() => import('./pages/family/FamilyHome'));
const FamilyProfile = lazyPage(() => import('./pages/family/Profile'));
const TeacherHome = lazyPage(() => import('./pages/teacher/TeacherHome'));
const TeacherProfile = lazyPage(() => import('./pages/teacher/Profile'));
const TeacherAdministrative = lazyPage(() => import('./pages/teacher/Administrative'));
const FamilyDocuments = lazyPage(() => import('./pages/family/Documents'));
const FamilySuccess = lazyPage(() => import('./pages/family/Success'));
const AdminToken = lazyPage(() => import('./pages/admin/AdminToken'));
const AdminUsers = lazyPage(() => import('./pages/admin/Users'));
const AdminCatalog = lazyPage(() => import('./pages/admin/Catalog'));
const AdminAnnouncements = lazyPage(() => import('./pages/admin/Announcements'));
const FamilyCancel = lazyPage(() => import('./pages/family/Cancel'));
const FamilySchoolYearConfirm = lazyPage(() => import('./pages/family/SchoolYearConfirm'));
const FamilyBuddles = lazyPage(() => import('./pages/family/Buddles'));
const FamilyEsercizi = lazyPage(() => import('./pages/family/Exercises'));
const TeacherEsercizi = lazyPage(() => import('./pages/teacher/Exercises'));

const MAINTENANCE_MODE = process.env.REACT_APP_MAINTENANCE_MODE === 'true';

const renderPage = (Page: React.ComponentType) =>
  MAINTENANCE_MODE ? <MaintenancePage /> : <MainContent Page={Page} />;

function App() {
  return (
    <>
      <Router>
        <ScrollToTop />
        <RouteTracker />
        <LoadingProvider>
          <ErrorProvider>
            <ToastProvider>
              <NotificationsProvider>
              <Routes>
                <Route path="/" element={<MainContent Page={Home} />} />
                <Route path="/chi-siamo" element={<MainContent Page={About} />} />
                <Route path="/prezzi" element={<Navigate to="/#prezzi" replace />} />
                <Route
                  path="/privacy-policy"
                  element={<ExternalRedirect to="https://www.iubenda.com/privacy-policy/45594522" />}
                />
                <Route
                  path="/cookie-policy"
                  element={<ExternalRedirect to="https://www.iubenda.com/privacy-policy/45594522/cookie-policy" />}
                />
                <Route path="/lavora-con-noi" element={<MainContent Page={WorkWithUs} />} />
                <Route path="/teacher-application" element={<MainContent Page={TeacherApplication} />} />
                <Route path="/login" element={renderPage(Login)} />
                <Route path="/forgot-password" element={renderPage(ForgotPassword)} />
                <Route path="/register" element={renderPage(Registration)}>
                  <Route path="" element={<Signup />} />
                  <Route path="confirm" element={<RegistrationConfirm />} />
                  <Route path="teacher" element={<TeacherRegistration />} />
                </Route>
                <Route path="/family">
                  <Route path="Home" element={renderPage(FamilyHome)} />
                  <Route path="profile" element={renderPage(FamilyProfile)} />
                  <Route path="documents" element={renderPage(FamilyDocuments)} />
                  <Route path="buddles" element={renderPage(FamilyBuddles)} />
                  <Route path='esercizi' element={renderPage(FamilyEsercizi)} />
                  <Route path="order/success" element={renderPage(FamilySuccess)} />
                  <Route path="order/cancel" element={renderPage(FamilyCancel)} />
                  <Route path="school-year/confirm" element={renderPage(FamilySchoolYearConfirm)} />
                </Route>
                <Route path="/teacher">
                  <Route path="home" element={renderPage(TeacherHome)} />
                  <Route path="profile" element={renderPage(TeacherProfile)} />
                  <Route path="administrative" element={renderPage(TeacherAdministrative)} />
                  <Route path="esercizi" element={renderPage(TeacherEsercizi)} />
                </Route>
                <Route path="/admin">
                  <Route path="home" element={<MainContent Page={AdminHomePage} />} />
                  <Route path="token" element={<MainContent Page={AdminToken} />} />
                  <Route path="users" element={<MainContent Page={AdminUsers} />} />
                  <Route path="catalog" element={<MainContent Page={AdminCatalog} />} />
                  <Route path="announcements" element={<MainContent Page={AdminAnnouncements} />} />
                  <Route path="login" element={<MainContent Page={AdminLogin} />} />
                  <Route index element={<Navigate to="/admin/home" />} />
                </Route>
                <Route path="*" element={renderPage(NotFound)} />
              </Routes>
              </NotificationsProvider>
            </ToastProvider>
          </ErrorProvider>
        </LoadingProvider>
      </Router>
    </>
  );
}

export default App;
